import React, { useEffect, useState, useRef } from 'react';
import { Alert, Card, Container, Row, Col, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ArrowDownCircle, ArrowUpCircle } from 'react-bootstrap-icons';
import FormGenerator from '../../form-generator-v2';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { addDocHubFormFieldsToForm, getObjectForPrefill, getModelOptions } from '../../helpers/formHelpers';
import File from '../common/file';
import Image from '../common/Image';
import SubmitButtonRow from '../common/SubmitButtonRow';
import { addChoicesToInventoryForm, formJson, validateAndCleanupForm } from './form';
import { updateSizesAccordingToSections } from '../../helpers/global';
import {
  applyCommissionDefaultsToFormValues,
  createCommissionResetHandlers,
  toCommissionTableRows
} from './inventoryCommissionHelpers';
import { AddEditModal, getForm, prepareModelFormForApi, validateModelForm } from '../admin/ManufacturerModels';
import { modelAddRows, modelUpdateRows } from '../admin/constants';
import { SKIRTING_TYPE_OPTIONS, getSkirtingAllowanceByType, getAllowanceByItemName } from '../pipeline/CalculationWorksheet';
import {
  INVENTORY_DELETION_ROWS,
  buildInventoryFormValuesWithWorksheetDefaults,
  buildWorksheetPayloadFromForm
} from './inventoryWorksheetHelpers';

const getFirstValueOfAppChoice = (appChoices = [], appChoiceKey) => {
  const choice = appChoices.find(a => a.key === appChoiceKey);
  return choice.values[0]._id || choice.values[0];
};

const sanitizePrefillUnit = prefillUnit => {
  if (!prefillUnit) return prefillUnit;
  const { _id, ...rest } = prefillUnit;
  return rest;
};

const getDefaultValues = (appChoices, prefillUnit) => {
  const hasPrefill = prefillUnit && Object.keys(prefillUnit).length > 0;
  if (hasPrefill) {
    const base = getObjectForPrefill(sanitizePrefillUnit(prefillUnit));
    const mappedWorksheet = buildInventoryFormValuesWithWorksheetDefaults(prefillUnit);
    return {
      ...base,
      ...mappedWorksheet,
      commissionSheetOptions: toCommissionTableRows(base.commissionSheetOptions),
      commissionSheetLessOptions: toCommissionTableRows(base.commissionSheetLessOptions)
    };
  }
  const base = {
    numberOfSections: 1,
    isStockInOrder: 'No',
    isRSO: 'No',
    unitLocation: getFirstValueOfAppChoice(appChoices, 'salesLocation'),
    manufacturer: getFirstValueOfAppChoice(appChoices, 'manufacturer'),
    exteriorSiding: getFirstValueOfAppChoice(appChoices, 'Exterior Siding'),
    hasFurnitureInside: 'No',
    type: getFirstValueOfAppChoice(appChoices, 'Type'),
    floorplanLender: getFirstValueOfAppChoice(appChoices, 'floorplanLender'),
    lotSet: 'No',
    typeOfUnit: getFirstValueOfAppChoice(appChoices, 'Type Of Unit'),
    roofLoad: '20 PSF'
  };

  const mappedWorksheet = buildInventoryFormValuesWithWorksheetDefaults({ numberOfSections: base.numberOfSections });
  return applyCommissionDefaultsToFormValues(
    {
      ...base,
      ...mappedWorksheet
    },
    appChoices
  );
};

const CreateInventory = ({ appChoices, onInventoryAdd, prefillUnit, isDuplicating, refetchChoices }) => {
  const formRef = useRef();
  const [form, setForm] = useState(null);
  const [pendingModelIdAfterRefetch, setPendingModelIdAfterRefetch] = useState(null);

  const [isCalculationWorkSheetVisible, setIsCalculationWorkSheetVisible] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [formValues, setFormValues] = useState(getDefaultValues(appChoices, prefillUnit));
  const [modelModalMeta, setModelMetaState] = useState({
    show: false,
    loading: false,
    defaultValues: null,
    disclaimer: ''
  });

  const updateSizes = (sizeOverall, numberOfSections) => {
    const sizes = updateSizesAccordingToSections(sizeOverall, Number(numberOfSections));
    setFormValues({ ...formValues, sizeOverall, numberOfSections, ...sizes });
  };

  const onNumberOfSectionsChange = numberOfSections => {
    const nextNumberOfSections = Number(numberOfSections);
    setFormValues(prev => {
      const sizes = updateSizesAccordingToSections(prev.sizeOverall, nextNumberOfSections);
      const worksheetDefaults = buildInventoryFormValuesWithWorksheetDefaults({ numberOfSections: nextNumberOfSections });
      return applyCommissionDefaultsToFormValues(
        {
          ...prev,
          numberOfSections: nextNumberOfSections,
          ...sizes,
          ...worksheetDefaults
        },
        appChoices
      );
    });
  };

  const onUnitLocationChange = unitLocation => {
    setFormValues(prev =>
      applyCommissionDefaultsToFormValues({ ...prev, unitLocation }, appChoices)
    );
  };

  const onSizeOverallChange = sizeOverall => {
    updateSizes(sizeOverall, formValues.numberOfSections);
  };

  const onManufacturerChange = manufacturerId => {
    const { options, values } = getModelOptions(appChoices, manufacturerId);
    setFormValues(prev => ({
      ...prev,
      model: values?.[0] || '',
      modelOptions: options,
      modelOptionValues: values
    }));
  };

  // on model changes will updated formValue
  const onModelChange = modelId => {
    const allModels = (appChoices.find(a => a.key === 'model')?.values || []).filter(Boolean);
    const model = allModels.find(m => m._id === modelId);
    if (!model) return;

    setFormValues(prev => {
      const next = { ...prev };
      if (model.boxSize) {
        next.sizeOverall = model.boxSize;
      }
      if (model.bedRooms !== undefined) next.bedRooms = model.bedRooms;
      if (model.bathRooms !== undefined) next.bathRooms = model.bathRooms;
      if (model.livingRooms != null) next['livingRooms-additionalDochubField'] = model.livingRooms;
      if (model.numberOfSections !== undefined) next.numberOfSections = model.numberOfSections;

      // Update sizes based on sizeOverall and numberOfSections (use model values if available, otherwise existing form values)
      const sizeOverallToUse = next.sizeOverall || prev.sizeOverall;
      const numberOfSectionsToUse = next.numberOfSections !== undefined ? next.numberOfSections : prev.numberOfSections;
      if (sizeOverallToUse) {
        Object.assign(next, updateSizesAccordingToSections(sizeOverallToUse, Number(numberOfSectionsToUse || 1)));
      }

      return next;
    });
  };

  // Sync form and select model after app choices refetch completes
  useEffect(() => {
    if (pendingModelIdAfterRefetch !== null) {
      // Refresh model options for current manufacturer
      const { options, values } = getModelOptions(appChoices, formValues.manufacturer);

      setFormValues(prev => ({
        ...prev,
        model: pendingModelIdAfterRefetch,
        modelOptions: options,
        modelOptionValues: values
      }));

      if (pendingModelIdAfterRefetch) {
        // Use setTimeout to ensure formValues update completes first
        setTimeout(() => {
          onModelChange(pendingModelIdAfterRefetch);
        }, 0);
      }

      setPendingModelIdAfterRefetch(null);
    }
  }, [pendingModelIdAfterRefetch]);

  const onCreateModelClick = newValue => {
    // Get manufacturer name from appChoices
    const manufacturerChoice = appChoices.find(a => a.key === 'manufacturer');
    const manufacturer = manufacturerChoice?.values.find(m => m._id === formValues.manufacturer);
    const manufacturerName = manufacturer?.factoryShortName || manufacturer?.name || 'this manufacturer';

    setModelMetaState({
      show: true,
      loading: false,
      defaultValues: {
        modelName: newValue,
        manufacturer: formValues.manufacturer,
        bathRooms: formValues.bathRooms,
        bedRooms: formValues.bedRooms,
        livingRooms: formValues['livingRooms-additionalDochubField'],
        boxSize: formValues.sizeOverall,
        numberOfSections: formValues.numberOfSections
      },
      disclaimer: `This Model will be added to the ${manufacturerName} Manufacturer`
    });
  };

  // Handle model form submission
  const handleAddModelSubmit = async form => {
    setModelMetaState(prev => ({ ...prev, loading: true }));

    // Reuse shared model validation (includes name/number + boxSize normalization)
    if (!validateModelForm(form)) {
      setModelMetaState(prev => ({ ...prev, loading: false }));
      return false;
    }

    const { photos, cleanForm } = prepareModelFormForApi(form);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_BASE,
      requestBody: {
        ...cleanForm,
        photos,
        manufacturer: modelModalMeta.defaultValues?.manufacturer || formValues.manufacturer
      }
    });

    if (error) {
      toast.error(error);
      setModelMetaState(prev => ({ ...prev, loading: false }));
      return false;
    }

    if (response) {
      if (response.livingRooms != null) setFormValues(prev => ({ ...prev, 'livingRooms-additionalDochubField': response.livingRooms }));
      const newlyCreatedModelId = response._id;
      await refetchChoices();
      setPendingModelIdAfterRefetch(newlyCreatedModelId);
    }

    // Close modal
    setModelMetaState({
      show: false,
      loading: false,
      defaultValues: null,
      disclaimer: ''
    });
    toast.success(response?.reusedExisting ? 'Existing model selected. Its saved details were preserved.' : 'Model created successfully!');

    return true;
  };

  const setUpForm = () => {
    const form = formJson({
      numberOfSections: Number(formValues.numberOfSections),
      onNumberOfSectionsChange,
      onSizeOverallChange,
      onManufacturerChange,
      onModelChange,
      onCreateModelClick,
      onUnitLocationChange
    });

    const newFormJson = addDocHubFormFieldsToForm({
      formJson: form,
      appChoices,
      model: 'Inventory'
    });
    addChoicesToInventoryForm({
      appChoices,
      form: newFormJson,
      modelOptions: formValues.modelOptions,
      modelOptionValues: formValues.modelOptionValues,
      unitLocationId: formValues.unitLocation,
      numberOfSections: formValues.numberOfSections,
      commissionResetHandlers: createCommissionResetHandlers(appChoices, setFormValues)
    });

    setForm(newFormJson.forms[0]);
  };

  const handleWorksheetFieldChange = key => event => {
    const { value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    setUpForm();
  }, [formValues]);

  // Load models for initial manufacturer selection
  useEffect(() => {
    if (formValues.manufacturer) {
      onManufacturerChange(formValues.manufacturer);
    }
  }, []);

  // Set up window function (required by old FormGenerator)
  window['onNewModelFormSubmit'] = handleAddModelSubmit;

  const handleFormValuesChange = newValues => {
    setFormValues(newValues);
  };

  const onInventoryFormSubmit = async formData => {
    if (uploadedFiles.some(f => f.status === 'ERROR') || images.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }

    if (uploadedFiles.some(f => f.status !== 'UPLOADED') || images.some(f => f.status !== 'UPLOADED')) {
      return toast.error('Some of the files are still uploading to the server, please try again in a moment!');
    }

    const valid = validateAndCleanupForm(formData);
    if (!valid) return;

    // Shape flat worksheet fields into nested payload expected by backend
    Object.assign(formData, buildWorksheetPayloadFromForm(formData));

    // Remove non-persisted helper fields
    delete formData.comments;
    delete formData.modelOptions;
    delete formData.modelOptionValues;

    formData['files'] = uploadedFiles.map(f => f.id);

    formData['photos'] = images
      .filter(img => img.filename)
      .map(img => ({
        url: img.filename,
        uploadedAt: new Date()
      }));

    toast.info('Please wait, creating an inventory item...');
    setButtonsDisabled(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_BASE,
      requestBody: formData
    });
    setButtonsDisabled(false);

    if (error) {
      return toast.error(error);
    }

    toast(`Inventory created successfully!`, {
      type: 'success'
    });

    if (onInventoryAdd) {
      onInventoryAdd(response);
    } else {
      setTimeout(() => document.location.reload(), 2000);
    }
  };

  const handleSubmitClick = async () => {
    if (formRef.current) {
      await formRef.current.submitForm();
    }
  };

  return (
    <Container fluid className="py-2 px-md-3">
      {isDuplicating && (
        <Alert variant="primary" className="px-2 py-1 font-weight-bold midFont">
          Only duplicate if you are creating a new unit!
        </Alert>
      )}
      <Card>
        <Card.Body className="mx-4">
          {form && (
            <>
              <h3 className="mb-0 fw-bold">Add New Inventory</h3>
              <hr className="" />
              <FormGenerator
                ref={formRef}
                form={form}
                formValues={formValues}
                onFormValuesChange={handleFormValuesChange}
                onSubmit={onInventoryFormSubmit}
                formDisabled={buttonsDisabled}
                showProgress={buttonsDisabled}
              />
              <div className=" text-right">
                <h6
                  className="d-inline-block hover-underline midFont text-primary my-2"
                  onClick={() => setIsCalculationWorkSheetVisible(v => !v)}
                >
                  {isCalculationWorkSheetVisible ? (
                    <>
                      Hide Calculation Work Sheet Fields <ArrowUpCircle size={13} />
                    </>
                  ) : (
                    <>
                      Show Calculation Work Sheet Fields <ArrowDownCircle size={13} />
                    </>
                  )}
                </h6>
              </div>

              {isCalculationWorkSheetVisible && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Table bordered size="sm" className="smallFont w-100">
                        <thead>
                          <tr className="bg-primary text-white smallFont">
                            <th colSpan={2} className="text-center">Deletions (from Manufacturer&apos;s Invoice)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {INVENTORY_DELETION_ROWS.map(([label, key]) => (
                            <tr key={key}>
                              <td className="align-middle">{label}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={formValues[key] ?? ''}
                                  onChange={handleWorksheetFieldChange(key)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Table bordered size="sm" className="smallFont w-100">
                        <thead>
                          <tr className="bg-primary text-white smallFont">
                            <th colSpan={2} className="text-center">Adds</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="align-middle">Factory Freight</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.factoryFreight'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.factoryFreight')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Air Conditioner</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.airConditioner'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.airConditioner')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Tax From Invoice</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.taxFromInvoice'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.taxFromInvoice')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">HUD Dues/Fees</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.hudDuesFees'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.hudDuesFees')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Delivery &amp; Set</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.deliveryAndSet'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.deliveryAndSet')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Skirting Type</td>
                            <td>
                              <select
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.skirtingType'] ?? ''}
                                onChange={e => {
                                  const type = e.target.value;
                                  const isSingleWide = Number(formValues.numberOfSections || 1) === 1;
                                  const allowance = getSkirtingAllowanceByType(type, isSingleWide);
                                  setFormValues(prev => ({
                                    ...prev,
                                    'maxSalesPriceCalculatorAddFields.skirtingType': type,
                                    'maxSalesPriceCalculatorAddFields.skirting':
                                      allowance != null
                                        ? String(allowance)
                                        : prev['maxSalesPriceCalculatorAddFields.skirting'] ?? ''
                                  }));
                                }}
                              >
                                <option value="">— Select —</option>
                                {SKIRTING_TYPE_OPTIONS.map(opt => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Skirting: </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.skirting'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.skirting')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">
                              Steps/Decks:{' '}
                              (# of sets{' '}
                              <input
                                type="number"
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: '70px' }}
                                value={formValues['maxSalesPriceCalculatorAddFields.numberOfSets'] ?? ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  const sets = parseInt(val, 10) || 0;
                                  const isSingleWide = Number(formValues.numberOfSections || 1) === 1;
                                  const perSet =
                                    getAllowanceByItemName('Steps & Decks (Per Set)', isSingleWide) ?? 0;
                                  setFormValues(prev => ({
                                    ...prev,
                                    'maxSalesPriceCalculatorAddFields.numberOfSets': val,
                                    'maxSalesPriceCalculatorAddFields.stepsDecks': String(perSet * sets)
                                  }));
                                }}
                              />{' '}
                              )
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.stepsDecks'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.stepsDecks')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Dirt Pad</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.dirtPad'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.dirtPad')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Footers</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.footers'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.footers')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Trim Out/Tape &amp; Texture</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture'] ?? ''}
                                onChange={handleWorksheetFieldChange(
                                  'maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture'
                                )}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Electric Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.electricHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.electricHookup')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Water Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.waterHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.waterHookup')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Sewer Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.sewerHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.sewerHookup')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Gas Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.gasHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.gasHookup')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Gutters &amp; Downspouts</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.guttersAndDownspouts'] ?? ''}
                                onChange={handleWorksheetFieldChange(
                                  'maxSalesPriceCalculatorAddFields.guttersAndDownspouts'
                                )}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Other (Desc)</td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.other'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.other')}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Other Amount</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.otherAmount'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.otherAmount')}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      <Table bordered size="sm" className="smallFont mt-3 w-100">
                        <tbody>
                          <tr>
                            <td className="align-middle">Sales Tax (not on Invoice)</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues.maxSalesPriceSalesTax ?? ''}
                                onChange={e =>
                                  setFormValues(prev => ({
                                    ...prev,
                                    maxSalesPriceSalesTax: e.target.value
                                  }))
                                }
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                  <Table bordered size="sm" className="smallFont mt-3">
                    <thead>
                      <tr className="bg-primary text-white smallFont">
                        <th colSpan={4} className="text-center">Land Improvements</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={2} className="align-middle">Land Purchase Price / Payoff</td>
                        <td colSpan={2}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={formValues.maxSalesPriceLandPurchasePrice ?? ''}
                            onChange={e =>
                              setFormValues(prev => ({
                                ...prev,
                                maxSalesPriceLandPurchasePrice: e.target.value
                              }))
                            }
                          />
                        </td>
                      </tr>
                      {[
                        ['Basement', 'maxSalesPriceLandImprovements.basement', 'Grading', 'maxSalesPriceLandImprovements.grading'],
                        ['Brick/Block Skirting', 'maxSalesPriceLandImprovements.brickBlockSkirting', 'Permanent Foundation', 'maxSalesPriceLandImprovements.permanentFoundation'],
                        ['Carport', 'maxSalesPriceLandImprovements.carport', 'Permits', 'maxSalesPriceLandImprovements.permits'],
                        ['Deck', 'maxSalesPriceLandImprovements.deck', 'Porch', 'maxSalesPriceLandImprovements.porch'],
                        ['Driveway', 'maxSalesPriceLandImprovements.driveway', 'Septic', 'maxSalesPriceLandImprovements.septic'],
                        ['Electrical Hookup', 'maxSalesPriceLandImprovements.electricalHookup', 'Sewer Hookup', 'maxSalesPriceLandImprovements.sewerHookup'],
                        ['Footers', 'maxSalesPriceLandImprovements.footers', 'Water Hookup', 'maxSalesPriceLandImprovements.waterHookup'],
                        ['Garage', 'maxSalesPriceLandImprovements.garage', 'Well', 'maxSalesPriceLandImprovements.well'],
                        ['Gas Hookup', 'maxSalesPriceLandImprovements.gasHookup', 'Other', 'maxSalesPriceLandImprovements.other']
                      ].map(([labelLeft, keyLeft, labelRight, keyRight]) => (
                        <tr key={keyLeft}>
                          <td className="align-middle">{labelLeft}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={formValues[keyLeft] ?? ''}
                              onChange={e =>
                                setFormValues(prev => ({
                                  ...prev,
                                  [keyLeft]: e.target.value
                                }))
                              }
                            />
                          </td>
                          <td className="align-middle">{labelRight}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={formValues[keyRight] ?? ''}
                              onChange={e =>
                                setFormValues(prev => ({
                                  ...prev,
                                  [keyRight]: e.target.value
                                }))
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
              <File uploadedFiles={uploadedFiles} onUploadedFilesChange={setUploadedFiles} />
              <Image images={images} onImagesChange={setImages} containerClassName="mt-3" />
              <SubmitButtonRow
                disabled={buttonsDisabled}
                onSaveClick={handleSubmitClick}
                showGoToDocHub={false}
                bottom
              />
            </>
          )}
        </Card.Body>
      </Card>

      <AddEditModal
        existingModels={(appChoices.find(choice => choice.key === 'model')?.values || []).filter(model => String(model.manufacturer?._id || model.manufacturer) === String(modelModalMeta.defaultValues?.manufacturer || formValues.manufacturer))}
        onSelectExisting={model => {
          setFormValues(current => ({ ...current, model: model._id }));
          onModelChange(model._id);
          setModelMetaState({ show: false, loading: false, defaultValues: null, disclaimer: '' });
        }}
        show={modelModalMeta.show}
        onHide={() => {
          setModelMetaState({
            show: false,
            loading: false,
            defaultValues: null,
            disclaimer: ''
          });
        }}
        toBeEditedModel={null}
        showProgress={modelModalMeta.loading}
        addForm={getForm({ rows: modelAddRows, type: 'Add' })}
        updateForm={getForm({ rows: modelUpdateRows, type: 'Update' })}
        defaultValues={modelModalMeta.defaultValues}
        disclaimer={modelModalMeta.disclaimer}
      />
    </Container>
  );
};

export default CreateInventory;
